import React from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Spinner,
} from "@fluentui/react-components";
import { RootState } from "../../../redux/store";
import { formatGBP } from "../../../utils/currency";

const NAVY = "#205393";
const GRN = "#2b8a3e";
const GRNL = "#e6f9f0";
const RED = "#c92a2a";
const T1 = "#0f172a";
const T2 = "#475569";
const T3 = "#94a3b8";
const BRD2 = "#c8cdd5";
const SUR2 = "#f8fafc";
const ORG = "#e67700";

interface Props {
  open: boolean;
  selectedCount: number;
  totalAmount: number;
  remainingCount: number;
  applyError?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const ApplyConfirmModal: React.FC<Props> = ({
  open,
  selectedCount,
  totalAmount,
  remainingCount,
  applyError,
  onConfirm,
  onCancel,
}) => {
  const { receiptDate, loading } = useSelector((s: RootState) => s.commissionStatement);

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!loading.apply && !data.open) onCancel(); }}>
      <DialogSurface style={{ minWidth: "440px", maxWidth: "500px" }}>
        <DialogTitle style={{ fontSize: "14px", fontWeight: 700, color: T1, paddingBottom: "12px", borderBottom: `1px solid ${BRD2}` }}>
          Confirm — Apply Matched Receipts
        </DialogTitle>

        <DialogBody style={{ display: "flex", flexDirection: "column", gap: "12px", paddingTop: "14px" }}>

          {/* Summary banner — amount + count side by side */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1px", background: BRD2, border: `1px solid ${BRD2}`, borderRadius: "6px", overflow: "hidden" }}>
            <div style={{ background: GRNL, padding: "14px 16px" }}>
              <div style={{ fontSize: "10px", fontWeight: 600, color: GRN, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "4px" }}>
                Total Amount
              </div>
              <div style={{ fontSize: "22px", fontWeight: 700, color: GRN, fontFamily: "monospace", letterSpacing: "-0.5px" }}>
                {formatGBP(totalAmount)}
              </div>
            </div>
            <div style={{ background: "#fff", padding: "14px 16px", display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "center" }}>
              <div style={{ fontSize: "10px", fontWeight: 600, color: T3, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "4px" }}>
                Receipts
              </div>
              <div style={{ fontSize: "28px", fontWeight: 700, color: GRN, lineHeight: 1 }}>
                {selectedCount}
              </div>
            </div>
          </div>

          {/* Detail rows — bordered table style */}
          <div style={{ border: `1px solid ${BRD2}`, borderRadius: "6px", overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", borderBottom: `1px solid ${BRD2}` }}>
              <div style={{ padding: "9px 14px", background: SUR2, fontSize: "11px", fontWeight: 600, color: T2, textTransform: "uppercase", letterSpacing: "0.4px", display: "flex", alignItems: "center" }}>
                Receipt Date
              </div>
              <div style={{ padding: "9px 14px", fontSize: "13px", fontWeight: 600, color: T1, display: "flex", alignItems: "center" }}>
                {receiptDate || "—"}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr" }}>
              <div style={{ padding: "9px 14px", background: SUR2, fontSize: "11px", fontWeight: 600, color: T2, textTransform: "uppercase", letterSpacing: "0.4px", display: "flex", alignItems: "center" }}>
                Receipts to Apply
              </div>
              <div style={{ padding: "9px 14px", fontSize: "13px", fontWeight: 600, color: T1, display: "flex", alignItems: "center" }}>
                {selectedCount}
              </div>
            </div>
          </div>

          {/* Remaining rows notice */}
          {remainingCount > 0 && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", padding: "9px 12px", borderRadius: "5px", background: "#fff8e1", border: "1px solid #ffe082", fontSize: "12.5px", color: ORG }}>
              <span style={{ fontWeight: 700, flexShrink: 0 }}>Note:</span>
              <span style={{ color: T2 }}>
                {remainingCount} row{remainingCount !== 1 ? "s" : ""} will remain unreceipted in this upload.
              </span>
            </div>
          )}

          {/* Apply error */}
          {applyError && (
            <div style={{ padding: "9px 12px", borderRadius: "5px", background: "#ffe6e6", border: `1px solid ${RED}`, fontSize: "12.5px", color: RED }}>
              {applyError}
            </div>
          )}

        </DialogBody>

        <DialogActions style={{ paddingTop: "12px", borderTop: `1px solid ${BRD2}`, justifyContent: "flex-end", gap: "8px" }}>
          <button
            onClick={onCancel}
            disabled={loading.apply}
            style={{ background: "#fff", color: T2, border: `1px solid ${BRD2}`, borderRadius: "5px", fontSize: "12.5px", fontWeight: 500, padding: "0 16px", height: "32px", cursor: loading.apply ? "not-allowed" : "pointer", opacity: loading.apply ? 0.6 : 1 }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading.apply}
            style={{ background: loading.apply ? "#c8cdd5" : NAVY, color: "#fff", border: "none", borderRadius: "5px", fontSize: "12.5px", fontWeight: 500, padding: "0 18px", height: "32px", cursor: loading.apply ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            {loading.apply ? <Spinner size="tiny" /> : "Confirm & Apply"}
          </button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

export default ApplyConfirmModal;
